import React, { FunctionComponent, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'

import {
  Box,
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Typography
} from '../../../../components/v2/styled';
import { SnackCopied } from '../../../../components/v2/snacks/Copied';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import LinkIcon from '@mui/icons-material/Link';

import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';
import { User } from '../../../../models/User';
import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Per-action render config: the gate (runtime availability for the viewed
// client) and the row content (title/explanation keys + the action element).
// Kept here (UI concern) rather than in the helper, which only owns data/logic —
// same split as GotoLinks' LINK_RENDER. The whole widget is gated on User::User
// in BaseMapper, so gates here don't re-check type.
//
// Exclusive pairs (enable/disable weight trigger, unlink/relink, pass-reset
// request/link-shown) are one item each; `render` picks the right half from the
// client's state.
type Ctx = {
  object: User;
  mutationCustomAction: any;
  passResetLink?: string;
  passResetEmail?: string;
  setNewEpisodeWarningOpen: (b: boolean) => void;
  setCopyOpen: (b: boolean) => void;
  currentPractice: any;
  login: any;
  navigate: (path: string, opts?: any) => void;
  t: any;
  theme: any;
};

type ActionRender = {
  gate: (ctx: Ctx) => boolean;
  titleKey?: string;
  explanationKey?: string;
  render: (ctx: Ctx, item: any) => React.ReactNode;
};

const ACTION_RENDER: Record<string, ActionRender> = {
  read_card: {
    gate: () => true,
    render: (ctx, item) => {
      const { Icon } = item;
      return <Button
        label={ctx.t("general.eid.read_card_action")}
        id="read_card_action"
        fullwidth
        startIcon={<Icon />}
        sx={{marginTop: 0}}
        onClick={() => {
          try {
            sessionStorage.setItem('eid_target_user', String(ctx.object.id));
          } catch (_e) {}
          ctx.navigate('/eid/landing');
        }}
      />;
    },
  },

  increase_episode: {
    gate: () => true,
    render: (ctx, item) => {
      const { Icon } = item;
      return <Button
        label={ctx.t("coach.submenu.detail_episodes.cta_new_episode")}
        id="increase_episode"
        fullwidth
        startIcon={<Icon />}
        sx={{marginTop: 0}}
        onClick={() => ctx.setNewEpisodeWarningOpen(true)}
      />;
    },
  },

  pass_reset: {
    gate: () => true,
    // Titles + explanations are no longer shown in the actions widget —
    // commented (not deleted) for easy re-enable:
    // titleKey: 'coach.submenu.detail.password_reset',
    render: (ctx, item) => {
      const { Icon } = item;
      if (!ctx.passResetLink) {
        return <Button
          label={ctx.t("coach.general.buttons.request_pass_reset_link")}
          id="passreset"
          fullwidth
          startIcon={<Icon />}
          sx={{marginTop: 0}}
          onClick={() => {
            ctx.mutationCustomAction.mutate({
              formData: {
                object: 'user',
                class_action: 'custom_api_inquire_new_password',
                id: ctx.object.id
              },
              pass: true
            });
          }}
        />;
      }
      // link already available: show the copy block (email + link + copy icons)
      return <Box sx={{textAlign: 'left'}}>
        <Typography sx={{fontSize: '0.9rem'}} html>{ctx.t("coach.submenu.detail.password_reset_explanation")}</Typography>
        <Typography sx={{fontSize: '0.8rem', paddingLeft: 2}}>{ctx.passResetEmail} <ContentCopyIcon
          sx={{cursor: 'pointer', scale: '0.7', marginRight: 1, marginBottom: -1, color: ctx.theme.palette.primary.main}}
          onClick={(e) => {
            e.preventDefault();
            navigator.clipboard.writeText(ctx.passResetEmail || '');
            ctx.setCopyOpen(true);
          }}
        /></Typography>
        <Typography sx={{fontSize: '0.9rem', marginTop: 1}} html>{ctx.t("coach.submenu.detail.password_reset_explanation_link")}</Typography>
        <Typography sx={{fontSize: '0.8rem', wordBreak: 'break-all', paddingLeft: 2}}>{ctx.passResetLink} <ContentCopyIcon
          sx={{cursor: 'pointer', scale: '0.7', marginRight: 1, marginBottom: -1, color: ctx.theme.palette.primary.main}}
          onClick={(e) => {
            e.preventDefault();
            navigator.clipboard.writeText(ctx.passResetLink || '');
            ctx.setCopyOpen(true);
          }}
        /></Typography>
      </Box>;
    },
  },

  disable_mfa: {
    gate: (ctx) => ctx.object.mfa_status === 1,
    // titleKey: 'coach.personal_settings.security.two_factor.title',
    // explanationKey: 'coach.personal_settings.security.two_factor.user_detail_line',
    render: (ctx, item) => {
      const { Icon } = item;
      return <Button
        label={ctx.t("coach.general.buttons.disable_mfa")}
        id="2fa_disable"
        secondary
        destroyBtn
        fullwidth
        startIcon={<Icon />}
        sx={{marginTop: 0}}
        onClick={() => {
          ctx.mutationCustomAction.mutate({
            formData: {
              object: 'user',
              class_action: 'custom_api_mfa_actions',
              id: ctx.object?.id,
              mfa_action: 'disable'
            },
            mfa: true
          });
        }}
      />;
    },
  },

  gdpr_json: {
    gate: () => true,
    // titleKey: 'coach.personal_settings.security.two_factor.title',
    // explanationKey: 'coach.personal_settings.security.two_factor.user_detail_line',
    render: (ctx, item) => {
      const { Icon } = item;
      return <Button
        label={ctx.t("coach.general.buttons.download_gdpr_json")}
        id="download_gdpr_json"
        fullwidth
        startIcon={<Icon />}
        sx={{marginTop: 0}}
        onClick={() => {
          ctx.mutationCustomAction.mutate({
            formData: {
              object: 'user',
              class_action: 'custom_api_get_gdpr_json',
              id: ctx.object?.id,
              practice_id: ctx.currentPractice?.id
            },
            start_download: true
          });
        }}
      />;
    },
  },

  weight_trigger: {
    gate: (ctx) => !ctx.object.extra_data?.weight_trigger || ctx.object.extra_data?.weight_trigger === ctx.currentPractice?.id,
    // titleKey: 'coach.submenu.detail.weight_trigger',
    // explanationKey: 'coach.submenu.detail.weight_trigger_explanation',
    render: (ctx, item) => {
      const { Icon } = item;
      if (!ctx.object.extra_data?.weight_trigger) {
        return <Button
          label={ctx.t("coach.general.actions.enable_weight_trigger")}
          id="enable_weight_trigger"
          fullwidth
          startIcon={<Icon />}
          sx={{marginTop: 0}}
          onClick={() => {
            ctx.mutationCustomAction.mutate({
              formData: {
                object: 'user',
                class_action: 'custom_api_weight_trigger',
                id: ctx.object?.id,
                practice_id: ctx.currentPractice?.id
              },
              mfa: true
            });
          }}
        />;
      }
      return <Button
        label={ctx.t("coach.general.actions.disable_weight_trigger")}
        id="disable_weight_trigger"
        fullwidth
        startIcon={<Icon />}
        sx={{marginTop: 0}}
        onClick={() => {
          ctx.mutationCustomAction.mutate({
            formData: {
              object: 'user',
              class_action: 'custom_api_weight_trigger',
              id: ctx.object?.id,
              practice_id: ctx.currentPractice?.id,
              disable: 1
            },
            mfa: true
          });
        }}
      />;
    },
  },

  goto_merge: {
    gate: (ctx) => !ctx.object.merge_pending,
    // titleKey: 'coach.submenu.detail.merge_request',
    // explanationKey: 'coach.submenu.detail.merge_request_explanation',
    render: (ctx, item) => {
      const { Icon } = item;
      return <Button
        label={ctx.t("coach.general.actions.to_merge_request")}
        id="goto_merge"
        secondary
        fullwidth
        startIcon={<Icon />}
        sx={{marginTop: 0}}
        onClick={() => ctx.navigate(`/user/${ctx.object.id}/merge_request`)}
      />;
    },
  },

  unlink: {
    gate: () => true,
    render: (ctx, item) => {
      if (!ctx.object.consent_status?.is_blocked) {
        const { Icon } = item; // LinkOffIcon
        return <Box sx={{textAlign: 'center'}}>
          <Button
            label={ctx.t("coach.submenu.detail.unlink_from_practice")}
            id="unlink_user"
            secondary
            destroyBtn
            startIcon={<Icon />}
            sx={{marginTop: 0}}
            onClick={() => {
              ctx.mutationCustomAction.mutate({
                formData: {
                  object: 'user',
                  class_action: 'custom_api_unlink_from_practice',
                  id: ctx.object?.id,
                  practice_id: (ctx.login?.practice_id || ctx.currentPractice?.id)
                },
                unlink: true
              });
            }}
          />
        </Box>;
      }
      return <Box sx={{textAlign: 'center'}}>
        <Button
          label={ctx.t("coach.submenu.detail.ununlink_from_practice")}
          id="link_user"
          startIcon={<LinkIcon />}
          sx={{marginTop: 0}}
          onClick={() => {
            ctx.mutationCustomAction.mutate({
              formData: {
                object: 'user',
                class_action: 'custom_api_unlink_from_practice',
                id: ctx.object?.id,
                practice_id: (ctx.login?.practice_id || ctx.currentPractice?.id),
                reverse: 1
              },
              unlink: true
            });
          }}
        />
      </Box>;
    },
  },

  // edit_user: mirrored affordance. Same gate + navigate target as the
  // bottom CTA in the details_contact widget, surfaced from the actions
  // column too. Independent toggle — both placements can be on/off.
  edit_user: {
    gate: (ctx) => ctx.object?.type === 'User::User' && !!ctx.object.consent_status?.is_basic,
    render: (ctx, item) => {
      const { Icon } = item;
      return <Button
        label={ctx.t("coach.general.actions.edit_user")}
        id="edit_user"
        fullwidth
        startIcon={<Icon />}
        sx={{marginTop: 0}}
        onClick={() => {
          ctx.navigate(`/user/${ctx.object.id}`, {state: {detail: true}});
        }}
      />;
    },
  },
};

type Props = {
  object: User;
  mutationCustomAction: any;
  passResetLink?: string;
  passResetEmail?: string;
  setNewEpisodeWarningOpen: (b:boolean) => void;
}

export const CoachUserWidgetsActions: FunctionComponent<Props> = ({
  object,
  mutationCustomAction,
  passResetLink, passResetEmail,
  setNewEpisodeWarningOpen
}) => {
  const [login] = useAtom(loginAtom);
  const [currentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t} = useTranslation(['translations']);

  const [copyOpen, setCopyOpen] = useState<boolean>(false);
  const [showAll, setShowAll] = useState<boolean>(false);

  // Actions settings are a coach preference, stored alongside the widget layout
  // on login.settings.userdetails_widgets.settings. `actions` is the show/hide
  // map (shown -> primary group, hidden -> behind "show all"); `actions_order`
  // (optional) overrides the default order.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const visibility = widgetSettings?.[HelpersClientWidgets.actionsValueKey];
  const order = widgetSettings?.[HelpersClientWidgets.actionsOrderKey];

  const ctx: Ctx = {
    object, mutationCustomAction, passResetLink, passResetEmail,
    setNewEpisodeWarningOpen, setCopyOpen, currentPractice, login, navigate, t, theme
  };

  // Ordered + state-tagged items from the coach's preference, then filtered by
  // each action's runtime gate (per-client state). A single ordered list is
  // rendered: shown items always appear; hidden items are woven into their
  // saved-order position and toggled on/off by the "show all" bool (still
  // accessible — actions can be important and this is often the only place to
  // reach them, e.g. password reset while on the phone).
  const ordered = HelpersClientWidgets.getOrderedActions(order, visibility);
  const renderable = ordered.filter((item) => {
    const cfg = ACTION_RENDER[item.key];
    return !!cfg && cfg.gate(ctx);
  });
  const hasHidden = renderable.some((i) => i.state === 'hidden');

  const renderRow = (item: any) => {
    const cfg = ACTION_RENDER[item.key];
    if (!cfg) return null;
    return <ActionRow
      key={item.key}
      title={cfg.titleKey ? t(cfg.titleKey) : undefined}
      explanation={cfg.explanationKey ? t(cfg.explanationKey) : undefined}
    >
      {cfg.render(ctx, item)}
    </ActionRow>;
  };

  return <Paper sx={{padding: 2, marginTop: 1}}>
    <Grid container spacing={1} sx={{position: 'relative'}}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.actions")}</Typography>
        <Divider />
      </Grid>

      {renderable.map((item) =>
        item.state === 'shown' || showAll ? renderRow(item) : null
      )}

      {hasHidden && <Grid item xs={12} sx={{textAlign: 'center', marginTop: 1}}>
        <Button
          label={showAll
            ? t("coach.personal_settings.userdetail_widgets.actions.show_less")
            : t("coach.personal_settings.userdetail_widgets.actions.show_all")}
          id="actions_show_all"
          text
          sx={{marginTop: 0}}
          endIcon={showAll ? <ExpandLess /> : <ExpandMore />}
          onClick={() => setShowAll(!showAll)}
        />
      </Grid>}
    </Grid>

    <SnackCopied open={copyOpen} setOpen={setCopyOpen} />
  </Paper>;
};

// Uniform row primitive: optional title (subtitle2) + optional explanation
// (caption) + content (button or block). Every action renders through this so
// the widget has consistent vertical rhythm and no ad-hoc inline sub-headers.
type RowProps = {
  title?: string;
  explanation?: string;
  children: React.ReactNode;
};

const ActionRow: FunctionComponent<RowProps> = ({ title, explanation, children }) => {
  return <Grid item xs={12} sx={{marginTop: 1}}>
    {!!title && <Typography variant="subtitle2">{title}</Typography>}
    {!!explanation && <Typography variant="caption" sx={{display: 'block', color: 'text.secondary', marginBottom: 0.5}}>{explanation}</Typography>}
    {children}
  </Grid>;
};
